import { AlbumsEffects } from '@adminStore/albums';
import { DashboardEffects } from '@adminStore/dashboard';
import { DocumentsEffects as AdminDocumentsEffects } from '@adminStore/documents';
import { EventsEffects as AdminEventsEffects } from '@adminStore/events';
import { GuestsEffects } from '@adminStore/guests';
import { ProductCategoriesEffects } from '@adminStore/product-categories';
import { ProductsEffects } from '@adminStore/products';
import { PosSessionsEffects } from '@adminStore/pos-sessions';
import { LinksEffects } from '@adminStore/links';
import { AppSettingsEffects } from '@adminStore/app-settings';
import { ExtraExpensesEffects } from '@adminStore/extra-expenses';
import { RewardLogsEffects } from '@adminStore/reward-logs';
import { RewardsEffects } from '@adminStore/rewards';
import { TagsEffects } from '@adminStore/tags';
import { UsersEffects } from '@adminStore/users';
import { WorkLogsEffects } from '@adminStore/work-logs';
import { WorkTypesEffects } from '@adminStore/work-types';
import { isPlatformServer } from '@angular/common';
import { HttpClient, provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  isDevMode,
  PLATFORM_ID,
  provideZonelessChangeDetection,
} from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideApiConfiguration } from '@api/api.providers';
import { environment } from '@environment';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { TranslateLoader, TranslateModule, TranslationObject } from '@ngx-translate/core';
import { provideToastr } from 'ngx-toastr';
import { APP_CONFIG } from '@klubovna/shared-data';
import * as Sentry from '@sentry/angular';
import {
  authInterceptor,
  CUSTOM_DATE_FORMATS,
  CustomDateAdapter,
  DEFAULT_LANGUAGE,
  GlobalErrorHandlerService,
} from '@shared';
import { appReducers } from '@store/app.reducers';
import { UserEffects } from '@store/user/user.effects';

import { routes } from './app.routes';

export function translateLoaderFactory(http: HttpClient, platformId: object): TranslateLoader {
  const serverPort =
    typeof globalThis !== 'undefined' && (globalThis as any)?.process?.env?.PORT
      ? (globalThis as any).process.env.PORT
      : '4000';
  const serverPrefix = `http://127.0.0.1:${ serverPort }/assets/i18n/`;
  const browserPrefix = './assets/i18n/';
  const prefix = isPlatformServer(platformId) ? serverPrefix : browserPrefix;
  return {
    getTranslation: (lang: string) => http.get<TranslationObject>(`${ prefix }${ lang }.json`),
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),
    provideAnimations(),
    provideClientHydration(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerImmediately',
    }),
    provideStore(appReducers),
    provideEffects([ UserEffects, UsersEffects, AdminEventsEffects, TagsEffects, GuestsEffects, AlbumsEffects, WorkTypesEffects, RewardsEffects, WorkLogsEffects, RewardLogsEffects, DashboardEffects, AdminDocumentsEffects, ProductCategoriesEffects, ProductsEffects, PosSessionsEffects, LinksEffects, AppSettingsEffects, ExtraExpensesEffects ]),
    provideHttpClient(withFetch(), withInterceptors([ authInterceptor ])),
    provideApiConfiguration(),
    provideToastr({
      timeOut: 3000,
      positionClass: 'toast-top-right',
      preventDuplicates: true,
      progressBar: true,
      closeButton: true,
      enableHtml: true,
    }),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: translateLoaderFactory,
          deps: [ HttpClient, PLATFORM_ID ],
        },
        fallbackLang: DEFAULT_LANGUAGE,
      }),
      MatDialogModule,
    ),
    { provide: APP_CONFIG, useValue: environment },
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    { provide: MAT_DATE_LOCALE, useValue: 'sk-SK' },
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    ...(environment.sentryLogging ? [ Sentry.TraceService ] : []),
  ],
};

